import { createElement } from 'react';
import { useTranslation } from 'react-i18next';
import { SearchIcon } from 'lucide-react';

import type { CommandItem, CommandSource } from '@/shared/registry/types';

import { MIN_QUERY_LENGTH, useSearchPreview, type SearchHit } from './api';
import { PaletteFilters } from './components/PaletteFilters';
import { searchUrl } from './filters';
import { plainText } from './highlight';
import { PALETTE_MODULES, usePaletteExclusions, useResultIcon } from './modules';
import { usePaletteFilters, usePaletteSearchOptions } from './paletteFilters';

const PALETTE_SET: ReadonlySet<string> = new Set(PALETTE_MODULES);

/** Hits per group in the palette. */
const PER_GROUP = 4;

/** Hits of the one section a filter picked (the palette then shows that group alone, with more room). */
const PER_FOCUSED_GROUP = 8;

function ResultIcon({ module }: { module: string }) {
  const Icon = useResultIcon(module);
  return createElement(Icon, { className: 'size-4 text-muted-foreground', 'aria-hidden': true });
}

function toItem(hit: SearchHit): CommandItem {
  const snippet = plainText(hit.snippet);
  return {
    id: `search:${hit.module}:${hit.entityType}:${hit.entityId}`,
    label: hit.title,
    description: snippet ? snippet.slice(0, 140) : undefined,
    leading: createElement(ResultIcon, { module: hit.module }),
    to: hit.url,
  };
}

/** The live search under the palette's filters; all the groups share this one request. */
function usePalettePreview(query: string) {
  const options = usePaletteSearchOptions();
  return useSearchPreview(query, usePaletteExclusions(), true, options);
}

/** A palette group with the live hits of one module (all sources share one request). */
function moduleSource(module: string, order: number): CommandSource {
  return {
    id: `search:${module}`,
    headingKey: `search:modules.${module}`,
    order,
    minQueryLength: MIN_QUERY_LENGTH,
    useItems: (query) => {
      const preview = usePalettePreview(query);
      const focused = usePaletteFilters((s) => s.module);
      if (focused && focused !== module) return { items: [], isLoading: false };
      const items = (preview.data?.items ?? [])
        .filter((hit) => hit.module === module)
        .slice(0, focused ? PER_FOCUSED_GROUP : PER_GROUP)
        .map(toItem);
      return { items, isLoading: false };
    },
  };
}

/** Hits of modules without their own group. */
const otherSource: CommandSource = {
  id: 'search:other',
  headingKey: 'search:palette.other',
  order: 190,
  minQueryLength: MIN_QUERY_LENGTH,
  useItems: (query) => {
    const preview = usePalettePreview(query);
    const focused = usePaletteFilters((s) => s.module);
    if (focused) return { items: [], isLoading: false };
    const items = (preview.data?.items ?? [])
      .filter((hit) => !PALETTE_SET.has(hit.module))
      .slice(0, PER_GROUP)
      .map(toItem);
    return { items, isLoading: false };
  },
};

/**
 * "Search everywhere for …": opens the results page — with the palette's filters — (also while the live results load);
 * listed first. It owns the filter row and resets it when the palette closes.
 */
const searchAllSource: CommandSource = {
  id: 'search:all',
  headingKey: 'search:palette.heading',
  order: 200,
  minQueryLength: MIN_QUERY_LENGTH,
  // Enter after typing a query searches (the live results below arrive later and do not take the selection).
  first: true,
  toolbar: PaletteFilters,
  onClose: () => usePaletteFilters.getState().reset(),
  useItems: (query) => {
    const { t } = useTranslation('search');
    const preview = usePalettePreview(query);
    const { module, period, mine } = usePaletteFilters();
    const options = usePaletteSearchOptions();
    const q = query.trim();
    const total = preview.data?.facets.reduce((sum, f) => sum + f.count, 0);
    return {
      isLoading: preview.isFetching && !preview.data,
      items: [
        {
          id: 'search:all',
          label: t('palette.searchAll', { query: q }),
          description: total
            ? t('palette.results', { count: total })
            : preview.data
              ? t('palette.noResults')
              : undefined,
          icon: SearchIcon,
          keywords: [q],
          to: searchUrl(q, { module, period, author: mine ? options.author : null }),
        },
      ],
    };
  },
};

/** The search module's contribution to the ⌘K palette: "search everywhere" first, then live results by module. */
export const searchCommandSources: CommandSource[] = [
  ...PALETTE_MODULES.map((module, index) => moduleSource(module, 100 + index * 10)),
  otherSource,
  searchAllSource,
];
